// Kardex (022) — lo que va al PDF firmado, puro y testeable sin React ni Medplum.
//
// El papel imprime, por CADA medicamento prescrito (6 por hoja, 4 días en columnas), la descripción "dosis, vía, frecuencia" y, por cada
// administración, fecha + hora + responsable; arriba, "Alergia a medicamentos: SI / NO, Describa". Antes el PDF firmado solo llevaba las
// administraciones guardadas en ESE clic (no el Kardex del día), la alergia como una sola frase "Sí — lista"/"No — …" y las horas 0-7
// sin fecha (pertenecen al día calendario siguiente del día de enfermería). Ahora imprime el Kardex COMPLETO del día de enfermería
// elegido: cada prescripción con todas sus administraciones (las ya guardadas y las de este guardado), con fecha real, hora,
// dosis/vía, estado y responsable.
//
// La hoja del papel cubre 4 días (4 columnas HORA/RESPONSABLE por medicamento): el PDF imprime la hoja de 4 días de enfermería que
// termina en el día elegido, con un bloque por día y la fecha real de cada administración. El sistema sigue registrando un día de
// enfermería a la vez (el que elige la enfermera); los 3 días previos se leen de lo ya guardado.
import type { AllergyIntolerance, MedicationAdministration, MedicationRequest } from '@medplum/fhirtypes';
import { sumarDias } from './constantes020Datos';
import { diaEnfermeriaDe, etiquetaMedicamento, pautaDe, responsableDe } from './enfermeria';
import type { Par } from './index';

export interface TextosKardex {
  readonly alergiaPregunta: string;
  readonly alergiaSi: string;
  /** Respuesta "No": debe decir que es "sin alergias activas registradas" (no afirma que el paciente no sea alérgico). */
  readonly alergiaNo: string;
  readonly alergiaDescriba: string;
  readonly administrado: string;
  readonly noAdministrado: string;
  /** Leyenda del día (de la hoja) sin administraciones de un medicamento, p. ej. "— sin administraciones —". */
  readonly sinAdministraciones: string;
  readonly responsable: string;
  readonly otrasAdministraciones: string;
  /** Rótulo del par con las 4 fechas de la hoja. */
  readonly hoja: string;
  /** "Día" (de enfermería), rotula cada bloque diario de un medicamento. */
  readonly dia: string;
}

/** `aaaa-mm-dd HH:mm` en hora local — la fecha REAL de la administración (las horas 0-7 caen en el día calendario siguiente al día de enfermería). */
export function fechaHoraLocal(fecha: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())} ${p(fecha.getHours())}:${p(fecha.getMinutes())}`;
}

/**
 * Descripciones legibles de las alergias que van bajo "Alergia a medicamentos": excluye las anuladas (`entered-in-error`), las que
 * traen una categoría distinta de `medication` (alimentaria, ambiental…) y las sin texto legible. Las que no traen categoría SÍ entran:
 * este sistema no la captura (002/003 guardan solo el texto), así que exigirla dejaría fuera todas las reales.
 */
export function descripcionesAlergiaMedicamentos(alergias: readonly AllergyIntolerance[]): string[] {
  return alergias
    .filter((a) => a.verificationStatus?.coding?.some((c) => c.code === 'entered-in-error') !== true)
    .filter((a) => !a.category?.length || a.category.includes('medication'))
    .map((a) => (a.code?.text ?? a.code?.coding?.[0]?.display ?? '').trim())
    .filter((texto) => texto !== '');
}

/** SI/NO + Describa del papel. Sin alergias activas: "No" y sin descripción. */
export function paresAlergiaMedicamentos(textos: TextosKardex, alergias: readonly string[]): Par[] {
  const lista = alergias.map((a) => a.trim()).filter(Boolean);
  if (lista.length === 0) return [{ pregunta: textos.alergiaPregunta, respuesta: textos.alergiaNo }];
  return [
    { pregunta: textos.alergiaPregunta, respuesta: textos.alergiaSi },
    { pregunta: textos.alergiaDescriba, respuesta: lista.join(', ') },
  ];
}

function detalleDeAdministracion(ma: MedicationAdministration): string {
  const dosis = ma.dosage?.text?.trim();
  if (dosis) return dosis;
  return (ma.note ?? []).map((n) => n.text?.trim() ?? '').filter(Boolean).join(' · ');
}

/** "2026-03-06 02:15 · Administrado · 500 mg · IV · Responsable: Ana Pérez" (o "No administrado: motivo"). */
export function textoAdministracion(ma: MedicationAdministration, textos: TextosKardex): string {
  const cuando = ma.effectiveDateTime ? fechaHoraLocal(new Date(ma.effectiveDateTime)) : '—';
  // completed = administrado; not-done = no administrado (+ motivo); cualquier otro estado (stopped, on-hold, in-progress…) se imprime tal cual, nunca como "Administrado".
  const estado =
    ma.status === 'not-done' ? `${textos.noAdministrado}${ma.statusReason?.[0]?.text ? `: ${ma.statusReason[0].text}` : ''}` : ma.status === 'completed' ? textos.administrado : String(ma.status);
  return [cuando, estado, detalleDeAdministracion(ma), `${textos.responsable}: ${responsableDe(ma.performer)}`].filter(Boolean).join(' · ');
}

/** Orden cronológico; las que no traen fecha válida van al final (un NaN en el comparador dejaría el orden indefinido). */
function ordenadas(mas: readonly MedicationAdministration[]): MedicationAdministration[] {
  const instante = (ma: MedicationAdministration): number => {
    const t = Date.parse(ma.effectiveDateTime ?? '');
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return [...mas].sort((a, b) => (instante(a) === instante(b) ? 0 : instante(a) < instante(b) ? -1 : 1));
}

function idDePrescripcion(ma: MedicationAdministration): string | undefined {
  return /MedicationRequest\/([^/]+)/.exec(ma.request?.reference ?? '')?.[1];
}

/** Días de enfermería que cubre una hoja del papel (4 columnas de administración). */
export const DIAS_HOJA_022 = 4;

/** Los 4 días de enfermería de la hoja que termina en `dia` (el primero es el más antiguo). */
export function diasHoja022(dia: string): string[] {
  return Array.from({ length: DIAS_HOJA_022 }, (_, i) => sumarDias(dia, i - (DIAS_HOJA_022 - 1)));
}

/**
 * Kardex de la hoja de 4 días para el PDF (una hoja del papel = 4 columnas de día): un par por prescripción activa y por CADA día de la hoja
 * (su respuesta lista TODAS las de ese día en orden cronológico, con fecha real, hora, dosis/vía, estado y responsable),
 * precedido por un par con las 4 fechas de la hoja. El día sin administraciones sale igual, con la leyenda `sinAdministraciones` (el papel
 * imprime los 4 días de cada medicamento, también los vacíos: 4 bloques por prescripción). Las de prescripciones ya inactivas van en pares "Otras" por medicamento. Las administraciones fuera de
 * los 4 días no entran.
 */
export function paresKardexHoja(prescripciones: readonly MedicationRequest[], administraciones: readonly MedicationAdministration[], dias: readonly string[], textos: TextosKardex): Par[] {
  const enHoja = new Set(dias);
  // Las anuladas (entered-in-error) no son parte del Kardex. El día de enfermería de cada una se calcula de su fecha real (las 0-7 h son del día anterior);
  // sin fecha válida no se puede ubicar en ningún día de la hoja.
  const ubicadas = administraciones
    .filter((ma) => ma.status !== 'entered-in-error')
    .map((ma) => ({ ma, dia: Number.isNaN(Date.parse(ma.effectiveDateTime ?? '')) ? undefined : diaEnfermeriaDe(new Date(ma.effectiveDateTime!)) }))
    // Las de fuera de la hoja no entran; las sin fecha válida NO se pierden: van a "Otras" (con "—" de fecha).
    .filter((x) => x.dia === undefined || enHoja.has(x.dia));
  const activas = new Set(prescripciones.map((p) => p.id));
  const porPrescripcion = new Map<string, { ma: MedicationAdministration; dia: string }[]>();
  const otras: MedicationAdministration[] = [];
  for (const x of ubicadas) {
    const id = idDePrescripcion(x.ma);
    if (id && activas.has(id) && x.dia !== undefined) porPrescripcion.set(id, [...(porPrescripcion.get(id) ?? []), { ma: x.ma, dia: x.dia }]);
    else otras.push(x.ma);
  }

  const pares: Par[] = [{ pregunta: textos.hoja, respuesta: dias.join(' · ') }];
  for (const mr of prescripciones) {
    const propias = porPrescripcion.get(mr.id ?? '') ?? [];
    const pauta = pautaDe(mr);
    const nombre = `${etiquetaMedicamento(mr)}${pauta ? ` (${pauta})` : ''}`;
    // Un bloque por cada día de la hoja (como las 4 columnas del papel); el día sin administraciones se marca con la leyenda, nunca se omite.
    for (const dia of dias) {
      const delDia = ordenadas(propias.filter((x) => x.dia === dia).map((x) => x.ma));
      pares.push({ pregunta: `${nombre} · ${textos.dia} ${dia}`, respuesta: delDia.length > 0 ? delDia.map((ma) => textoAdministracion(ma, textos)).join(' | ') : textos.sinAdministraciones });
    }
  }

  // Prescripciones que ya no están activas (o sin request): se agrupan por medicamento para no perder ninguna administración de la hoja.
  const porNombre = new Map<string, MedicationAdministration[]>();
  for (const ma of ordenadas(otras)) {
    const nombre = ma.medicationCodeableConcept?.text ?? ma.medicationCodeableConcept?.coding?.[0]?.display ?? ma.medicationReference?.display ?? '—';
    porNombre.set(nombre, [...(porNombre.get(nombre) ?? []), ma]);
  }
  for (const [nombre, mas] of porNombre) pares.push({ pregunta: `${textos.otrasAdministraciones}: ${nombre}`, respuesta: mas.map((ma) => textoAdministracion(ma, textos)).join(' | ') });
  return pares;
}
